import { Input, Typography, Button } from '@mgmresorts/wcl-lab';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { requestForgotPassword } from '../../api/fetch/forgot-password';
import { Alert } from '../../components/ui/Alert';
import { Confirmation } from '../../components/ui/Confirmation';
import { Container } from '../../components/ui/Container';
import { NavigationLink } from '../../components/ui/Link';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import { useContent } from '../../hooks/useContent';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useInput } from '../../hooks/useInput';
import { captureEvent, captureException } from '../../monitoring/logrocket';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { MaxLengths } from '../../utils/constants';
import { PersistentParametersContext } from '../../utils/persistParameterContext';
import { redirectToSignIn } from '../../utils/redirect';
import { emailValidator } from '../../utils/validators';

import { StyledContent } from './styled';

export function ForgotPassword() {
  usePageLoadEvent('sign in', 'forgot password');
  const trackEvent = useEvent();
  const [isLoading, setIsLoadingState] = useState(false);
  const [transactionStep, setTransactionStep] = useState('init');
  const [error, setErrorState] = useState<null | string>(null);
  const { getContentByPath } = useContent();
  const { validateField, invalidFields } = useFieldValidation([emailValidator]);
  const { params } = useContext(PersistentParametersContext);

  const { value, bind } = useInput('', 'email', undefined, validateField);

  useEffect(() => {
    trackEvent('identity – forgot password - start', 'forgot password process start', 'forgotpstart');
  }, [trackEvent]);

  useEffect(() => {
    if (invalidFields.includes('email')) {
      trackEvent(
        'identity – forgot password - credentials input error',
        'forgot password credentials input error',
        'forgotpinputerror',
        {
          errorField: 'Email Address',
          errorDisplayed: getContentByPath('forgotPassword.steps.reset.fields.email.validation'),
        },
      );
    }
  }, [invalidFields, invalidFields.length, trackEvent, getContentByPath]);

  const requestPasswordReset = useCallback(async () => {
    try {
      captureEvent('Forgot Password', {
        email: String(value),
      });

      setIsLoadingState(true);
      await requestForgotPassword({ username: value });
      setTransactionStep('confirmation');

      trackEvent(
        'identity – forgot password - credentials submit success',
        'forgot password credentials submit success',
        'forgotpsubmitsuccess',
        { forgotPasswordState: 'success', emailValidity: 'true' },
      );

      if (error) {
        setErrorState(null);
      }
    } catch (error) {
      setErrorState(getContentByPath('forgotPassword.errors.default'));
      trackEvent(
        'identity – forgot password - credentials submit error',
        'forgot password credentials submit error',
        'forgotpsubmitfail',
        { forgotPasswordState: 'failed', errorDisplayed: getContentByPath('forgotPassword.errors.default') },
      );
      captureException(error as Error, 'forgot-password', { username: value });
    } finally {
      setIsLoadingState(false);
    }
  }, [value, error, getContentByPath, trackEvent]);

  const canRequestSignIn = useMemo(
    () => !invalidFields.length && value && !isLoading,
    [invalidFields.length, isLoading, value],
  );

  if (transactionStep === 'confirmation') {
    return (
      <Confirmation
        title={getContentByPath('forgotPassword.steps.confirmation.title')}
        description={getContentByPath<string>('forgotPassword.steps.confirmation.body').replace('<email>', value)}
        redirectCTA={
          params.webview
            ? undefined
            : {
                label: getContentByPath('forgotPassword.steps.confirmation.buttons.primaryAction.label'),
                action: async () => {
                  await trackEvent(
                    'identity – forgot password - back to sign in',
                    'back to sign in',
                    'fpbacktosignin',
                    {},
                    true,
                  );
                  redirectToSignIn();
                },
              }
        }
      />
    );
  }

  return (
    <StyledContent>
      <PageLayout
        testId='route-/forgot-password'
        title={getContentByPath('forgotPassword.steps.reset.title')}
        description={getContentByPath('forgotPassword.steps.reset.description')}
      >
        <Container height='auto'>
          <Logo className='identity__page-logo' />
          <Typography className='identity__page-heading' variant='h1'>
            {getContentByPath('forgotPassword.steps.reset.title')}
          </Typography>
          {error && <Alert text={error} />}
          <Input
            className={`identity__field email ${invalidFields.includes('email') ? 'with-error' : ''}`}
            label={getContentByPath('forgotPassword.steps.reset.fields.email.label')}
            id='email'
            aria-label={getContentByPath('forgotPassword.steps.reset.fields.email.label')}
            disabled={isLoading}
            error={invalidFields.includes('email')}
            hint={
              invalidFields.includes('email')
                ? getContentByPath('forgotPassword.steps.reset.fields.email.validation')
                : ''
            }
            {...bind}
            maxLength={MaxLengths.Email}
          />
          <Button
            sticky
            ariaLabel={getContentByPath('forgotPassword.steps.reset.buttons.primaryAction.label')}
            onClick={requestPasswordReset}
            onKeyPress={e => {
              e.preventDefault();
              if (e.key === 'Enter') {
                requestPasswordReset();
              }
            }}
            disabled={!canRequestSignIn}
            className='identity__form-submit'
            loading={isLoading}
          >
            <Typography>{getContentByPath('forgotPassword.steps.reset.buttons.primaryAction.label')}</Typography>
          </Button>
          {!params.webview && (
            <NavigationLink
              className='identity__secondary-action center'
              path='/'
              onClick={async () => {
                await trackEvent(
                  'identity – forgot password - back to sign in',
                  'back to sign in',
                  'fpbacktosignin',
                  {},
                  true,
                );
              }}
            >
              {getContentByPath('forgotPassword.steps.reset.buttons.secondaryAction.label')}
            </NavigationLink>
          )}
        </Container>
      </PageLayout>
    </StyledContent>
  );
}

ForgotPassword.displayName = 'ForgotPassword';
